import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useRecoilState } from 'recoil'
import {
  chatRichtextTestCompletionsUrl,
  TestChatCompletionsReq,
  TestChatGender,
  TestChatHistoryItem,
} from '@/apis/chatRichtextTest'
import { createStateFactory } from '@/store/create'
import useSSEMessageStream, { RawSSEEvent } from './useSSEMessageStream'
import { makeId } from '@/utils/func'

const MAX_REQUEST_HISTORY = 50

const toGenderValue = (gender: 'male' | 'female' | 'other' | null) => {
  if (gender === 'male') return 1
  if (gender === 'female') return 2
  return 3
}

type UseTestChatStreamProps = {
  isUpdateChar: boolean
  from: string
  characterId?: string | null
  name: string
  description: string
  opening: string
  background: string
  gender: 'male' | 'female' | 'other' | null
  outputFormatConfig?: string
  advancedSetCheck: boolean
  conversationStyle?: number
  dialogStyle?: Array<{ role: 'assistant' | 'user' | 'all'; message: string }>
  validateBeforeSend: () => boolean
  onFirstTcid?: (tcid: string) => void
}

export default function useTestChatStream({
  isUpdateChar,
  from,
  characterId,
  name,
  description,
  opening,
  background,
  gender,
  outputFormatConfig,
  advancedSetCheck,
  conversationStyle,
  dialogStyle,
  validateBeforeSend,
  onFirstTcid,
}: UseTestChatStreamProps) {
  const [charForm, setCharForm] = useRecoilState(
    createStateFactory(isUpdateChar, from || 'character_image')
  )
  const currentPendingIdRef = useRef<string | null>(null)
  const tcidRef = useRef(charForm.test_chat_tcid || '')

  useEffect(() => {
    tcidRef.current = charForm.test_chat_tcid || ''
  }, [charForm.test_chat_tcid])

  const appendTcidFromRaw = useCallback(
    (raw?: RawSSEEvent) => {
      const incomingTcid = typeof raw?.tcid === 'string' ? raw.tcid : ''
      if (!incomingTcid) return
      const isFirstTcid = !tcidRef.current
      tcidRef.current = incomingTcid
      setCharForm((prev) => {
        if (prev.test_chat_tcid) {
          return prev
        }
        return { ...prev, test_chat_tcid: incomingTcid }
      })
      if (isFirstTcid) {
        onFirstTcid?.(incomingTcid)
      }
    },
    [onFirstTcid, setCharForm]
  )

  const setPendingStatus = useCallback(
    (id: string, status: 'pending' | 'failed') => {
      setCharForm((prev) => ({
        ...prev,
        test_chat_pending: prev.test_chat_pending.map((item) =>
          item.id === id ? { ...item, status } : item
        ),
      }))
    },
    [setCharForm]
  )

  const {
    start: startStream,
    isStreaming,
    isWaitingForFirstChunk,
  } = useSSEMessageStream({
    url: chatRichtextTestCompletionsUrl,
    method: 'POST',
    enableInternalState: true,
    onMessageChunk: (chunk, raw) => {
      appendTcidFromRaw(raw)
      setCharForm((prev) => ({
        ...prev,
        test_chat_streaming: `${prev.test_chat_streaming || ''}${chunk}`,
      }))
    },
    onMessageEnd: (finalContent, raw) => {
      appendTcidFromRaw(raw)
      setCharForm((prev) => {
        const pendingId = currentPendingIdRef.current
        const pendingMessage = prev.test_chat_pending.find(
          (item) => item.id === pendingId
        )
        const nextHistory = [...prev.test_chat_history]
        if (pendingMessage) {
          nextHistory.push({ role: 'user', content: pendingMessage.content })
        }
        nextHistory.push({ role: 'assistant', content: finalContent || '' })
        return {
          ...prev,
          test_chat_history: nextHistory,
          test_chat_streaming: '',
          test_chat_pending: pendingId
            ? prev.test_chat_pending.filter((item) => item.id !== pendingId)
            : prev.test_chat_pending,
        }
      })
      currentPendingIdRef.current = null
    },
    onBusinessError: (_code, raw) => {
      appendTcidFromRaw(raw)
      setCharForm((prev) => ({
        ...prev,
        test_chat_streaming: '',
      }))
      const pendingId = currentPendingIdRef.current
      if (pendingId) {
        setPendingStatus(pendingId, 'failed')
      }
      currentPendingIdRef.current = null
    },
    onNetworkError: () => {
      setCharForm((prev) => ({ ...prev, test_chat_streaming: '' }))
      const pendingId = currentPendingIdRef.current
      if (pendingId) {
        setPendingStatus(pendingId, 'failed')
      }
      currentPendingIdRef.current = null
    },
  })

  const buildAndStartStream = useCallback(
    async (content: string, pendingId: string, appendPending: boolean) => {
      const trimmed = content.trim()
      if (!trimmed) return
      if (!validateBeforeSend()) return
      if (isStreaming || isWaitingForFirstChunk) return

      const pending = {
        id: pendingId,
        content: trimmed,
        status: 'pending' as const,
        timestamp: Math.floor(Date.now() / 1000),
      }

      let localHistory: TestChatHistoryItem[] = []
      setCharForm((prev) => {
        const hasAnyHistory = prev.test_chat_history.length > 0
        const openingMessage = opening.trim()
        localHistory = hasAnyHistory
          ? prev.test_chat_history
          : openingMessage
            ? [
                { role: 'assistant', content: openingMessage },
                ...prev.test_chat_history,
              ]
            : prev.test_chat_history

        return {
          ...prev,
          test_chat_locked: true,
          test_chat_first_tip_shown: prev.test_chat_first_tip_shown,
          test_chat_history: localHistory,
          test_chat_pending: appendPending
            ? [...prev.test_chat_pending, pending]
            : prev.test_chat_pending,
          test_chat_streaming: '',
        }
      })

      currentPendingIdRef.current = pendingId

      const req: TestChatCompletionsReq = {
        tcid: tcidRef.current,
        character_id: characterId || '0',
        name: name.trim(),
        description: description.trim(),
        opening: opening.trim(),
        background: background.trim(),
        gender: toGenderValue(gender) as TestChatGender,
        output_format_config: outputFormatConfig || '',
        conversation_style: advancedSetCheck ? (conversationStyle ?? 0) : 0,
        dialog_style: advancedSetCheck
          ? (dialogStyle || []).filter(
              ({ message }) => message.trim().length > 0
            )
          : [],
        history: localHistory.slice(-MAX_REQUEST_HISTORY),
        content: trimmed,
      }

      await startStream({
        body: req,
      })
    },
    [
      background,
      characterId,
      advancedSetCheck,
      conversationStyle,
      description,
      dialogStyle,
      gender,
      isStreaming,
      isWaitingForFirstChunk,
      name,
      opening,
      outputFormatConfig,
      setCharForm,
      startStream,
      validateBeforeSend,
    ]
  )

  const sendMessage = useCallback(
    async (content: string) => {
      const pendingId = makeId()
      await buildAndStartStream(content, pendingId, true)
    },
    [buildAndStartStream]
  )

  const resendMessage = useCallback(
    async (pendingId: string) => {
      const target = charForm.test_chat_pending.find(
        (item) => item.id === pendingId
      )
      if (!target) return
      setPendingStatus(pendingId, 'pending')
      await buildAndStartStream(target.content, pendingId, false)
    },
    [buildAndStartStream, charForm.test_chat_pending, setPendingStatus]
  )

  const resetHistoryOnly = useCallback(() => {
    setCharForm((prev) => ({
      ...prev,
      test_chat_history: [],
      test_chat_pending: [],
      test_chat_streaming: '',
      test_chat_locked: false,
    }))
    currentPendingIdRef.current = null
  }, [setCharForm])

  const resetSession = useCallback(() => {
    setCharForm((prev) => ({
      ...prev,
      test_chat_tcid: '',
      test_chat_history: [],
      test_chat_pending: [],
      test_chat_streaming: '',
      test_chat_locked: false,
      test_chat_first_tip_shown: false,
    }))
    currentPendingIdRef.current = null
  }, [setCharForm])

  const markFirstTipSeen = useCallback(() => {
    setCharForm((prev) =>
      prev.test_chat_first_tip_shown
        ? prev
        : { ...prev, test_chat_first_tip_shown: true }
    )
  }, [setCharForm])

  return {
    sendMessage,
    resendMessage,
    resetHistoryOnly,
    resetSession,
    markFirstTipSeen,
    isWaitingForFirstChunk,
    isStreaming,
    testChat: useMemo(
      () => ({
        tcid: charForm.test_chat_tcid || '',
        history: charForm.test_chat_history,
        pending: charForm.test_chat_pending,
        streamingContent: charForm.test_chat_streaming,
        isLocked: charForm.test_chat_locked,
        showFirstTip:
          charForm.test_chat_locked && !charForm.test_chat_first_tip_shown,
      }),
      [
        charForm.test_chat_first_tip_shown,
        charForm.test_chat_history,
        charForm.test_chat_locked,
        charForm.test_chat_pending,
        charForm.test_chat_streaming,
        charForm.test_chat_tcid,
      ]
    ),
  }
}
